
import { useState } from 'react';

export default function QuoteRequest() {
  const [formData, setFormData] = useState({
    category: '',
    productName: '',
    quantity: '',
    specifications: '',
    deliveryDate: '',
    deliveryLocation: '',
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    additionalNotes: '',
    urgency: '일반'
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    '어구류',
    '갑판용품',
    '기관물품',
    '안전장비',
    '통신장비',
    '냉동/냉장설비',
    '선용품',
    '기타'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d67k99dt1k61917cleq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          category: '',
          productName: '',
          quantity: '',
          specifications: '',
          deliveryDate: '',
          deliveryLocation: '',
          companyName: '',
          contactPerson: '',
          phone: '',
          email: '',
          additionalNotes: '',
          urgency: '일반'
        });
      }, 3000);
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">견적 요청</h2>
        <p className="text-gray-600 text-lg">
          필요하신 제품 정보를 입력하시면 최적의 제조업체에서 견적을 보내드립니다
        </p>
      </div>

      {/* Benefits */}
      <div className="grid md:grid-cols-4 gap-4 mb-12">
        <div className="flex items-start gap-3 p-5 bg-teal-50 rounded-2xl">
          <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">빠른 응답</h3>
            <p className="text-xs text-gray-600">평균 24시간 내 견적 회신</p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-5 bg-cyan-50 rounded-2xl">
          <div className="w-10 h-10 bg-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">검증된 업체</h3>
            <p className="text-xs text-gray-600">인증받은 제조업체만 연결</p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-5 bg-emerald-50 rounded-2xl">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-price-tag-3-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">최적 가격</h3>
            <p className="text-xs text-gray-600">여러 업체 비교 견적 가능</p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-5 bg-orange-50 rounded-2xl">
          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-customer-service-2-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 text-sm">전담 상담</h3>
            <p className="text-xs text-gray-600">1:1 맞춤 상담 지원</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form id="quote-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
        {/* Product Information */}
        <div className="bg-gray-50 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-box-3-line text-teal-600"></i>
            제품 정보
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제품 카테고리 <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">카테고리 선택</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제품명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
                placeholder="예: 스테인리스 와이어 로프"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                수량 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                placeholder="예: 100m, 50개"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                긴급도
              </label>
              <div className="flex gap-3">
                {['일반', '급함', '매우급함'].map((level) => (
                  <label
                    key={level}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                      formData.urgency === level
                        ? level === '매우급함'
                          ? 'bg-red-50 border-red-500 text-red-700'
                          : level === '급함'
                          ? 'bg-orange-50 border-orange-500 text-orange-700'
                          : 'bg-teal-50 border-teal-500 text-teal-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="urgency"
                      value={level}
                      checked={formData.urgency === level}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">{level}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                희망 납품일
              </label>
              <input
                type="date"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                배송 희망 지역 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="deliveryLocation"
                value={formData.deliveryLocation}
                onChange={handleChange}
                required
                placeholder="예: 부산광역시 영도구 ○○부두"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제품 사양 및 요구사항
              </label>
              <textarea
                name="specifications"
                value={formData.specifications}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                placeholder="제품의 상세 사양, 규격, 특별 요구사항 등을 입력해주세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.specifications.length}/500</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-user-line text-teal-600"></i>
            연락처 정보
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                회사명/선박명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                placeholder="예: ○○수산"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                담당자명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                required
                placeholder="이름 입력"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연락처 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="010-0000-0000"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="example@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                추가 요청사항
              </label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="기타 문의사항이나 요청사항을 입력해주세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.additionalNotes.length}/500</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-center pt-6">
          <button
            type="submit"
            disabled={submitted || isSubmitting}
            className="inline-flex items-center gap-2 px-12 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all shadow-lg shadow-teal-600/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                전송 중...
              </>
            ) : submitted ? (
              <>
                <i className="ri-check-line text-xl"></i>
                견적 요청 완료
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                견적 요청하기
              </>
            )}
          </button>
        </div>

        {submitted && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 text-center">
            <i className="ri-checkbox-circle-line text-4xl text-teal-600 mb-3"></i>
            <p className="text-teal-800 font-medium">
              견적 요청이 성공적으로 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
